import {
  ArrowClockwiseIcon,
  ArrowRightIcon,
  BookOpenIcon,
  ChartBarIcon,
  SlidersHorizontalIcon,
  UserIcon,
} from '@phosphor-icons/react';
import type { TFunction } from 'i18next';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnonymous, useAuthStore } from '~/store/authStore.js';
import { lastGameHref, type LastGame } from '~/store/lastGame.js';
import { useSettingsStore } from '~/store/settingsStore.js';
import { Avatar } from '~/ui/Avatar.js';
import { ButtonLink } from '~/ui/Button.js';
import { cx } from '~/ui/cx.js';
import { Lockup } from '~/ui/Lockup.js';
import { MenuTile } from '~/ui/MenuTile.js';
import { QuietGround } from '~/ui/QuietGround.js';
import { ResumeDoor, useResumeCheck } from '~/ui/ResumeDoor.js';
import { RoomChip } from '~/ui/RoomChip.js';
import { Screen } from '~/ui/Screen.js';
import { signInHref } from '~/ui/screens/useSessionNav.js';
import { useCrossInvitation } from '~/ui/useCrossInvitation.js';

/**
 * The front door: the lockup, the doors that start a game, and the rooms.
 *
 * Built from the `lobby-home` sketch's exhibit A, the poster Mitch chose on
 * 2026-09-07 (`docs/ux/arcade/home.md`). The lockup stands big over one lemon
 * Play door, Play again sits under it at the same width and height, and Learn,
 * Stats, Profile and Settings are round chips at the foot. It all stands on
 * the two paper suits, and the player's head waits alone in the corner.
 *
 * What the poster cut stays cut: no tagline, no twenty-nine hand, no hint on
 * Play, no Premium chip. Offline play is still one tap behind Play, which
 * offers bots and pass-and-play without an account, because that is the game
 * that always works.
 *
 * A game in progress outranks every new one. When the device holds one, the
 * Resume door takes the top of the stack and the lemon, Play goes white under
 * it, and Play again waits until the table is finished.
 *
 * A player who plays regular cribbage well and has never met the grid gets the
 * sketch's two-minute invitation under the doors (`useCrossInvitation`). Its
 * Learn Cross-Cribbage button takes the lemon, and Play goes white, so the
 * screen still has one lemon action. It never shows beside a Resume door.
 */
export function HomeScreen(): React.JSX.Element {
  const { t } = useTranslation();
  /* No account, whether or not the device has a session yet. The player wears
     the guest head, and the account chip asks them to sign in (Mitch,
     2026-09-30). */
  const anonymous = useAnonymous();
  const hasSession = useAuthStore((state) => state.token !== null);
  const lastGame = useSettingsStore((state) => state.lastGame);
  /* The poster waits, invisible but laid out, until the device says whether a
     game is waiting. Drawn before that, Play would paint lemon and then fade
     to white under the player's eye when Resume arrived. */
  const resumeCheck = useResumeCheck();
  const { resume, settled } = resumeCheck;
  /* The invitation also changes Play's color, so the poster waits for its
     answer too. Both settle by the same deadline. */
  const invitation = useCrossInvitation({ resumeCheck });
  /* A game in progress, or the invitation, takes the lemon, and Play goes
     white. */
  const play = resume === null && !invitation.show ? 'primary' : 'default';

  return (
    <div className="relative isolate grid min-h-full">
      <QuietGround className="-z-10" />
      <CornerHead guest={anonymous} />
      <Screen
        centered
        width="poster"
        className={cx(
          'w-full tablet:items-center',
          !(settled && invitation.settled) && 'invisible',
        )}
      >
        {/* On a phone the stack is centered in the space above the rooms,
            which stand at the foot. From the tablet breakpoint the stack and
            the rooms center together, as one poster. */}
        <div className="flex flex-1 flex-col justify-center tablet:flex-none">
          <div className="flex w-full flex-col tablet:w-fit">
            <Lockup className="self-center tablet:self-start" />
            <nav className="mt-10 flex flex-col gap-3 tablet:mt-11" aria-label={t('nav.play')}>
              {resume !== null && <ResumeDoor resume={resume} />}
              {/* Keyed on its variant, so Play is a new door when Resume
                  arrives. The same door would animate its face from lemon to
                  white in view: the answer lands on the render that reveals
                  the poster, and `transition-control` carries the color. */}
              <MenuTile
                key={play}
                size="poster"
                variant={play}
                to="/play"
                suit="spade"
                label={t('nav.play')}
              />
              {resume === null && lastGame !== null && (
                <AgainDoor game={lastGame} hasSession={hasSession} />
              )}
            </nav>
            {invitation.show && <CrossInvitation />}
          </div>
        </div>
        {/* The screen's gap plus 20 points sets the rooms 44 under the doors,
            the sketch's laptop. Under the invitation the gap alone, its 24. */}
        <nav
          className={cx(
            'flex justify-between px-1 tablet:justify-center tablet:gap-11 tablet:px-0',
            !invitation.show && 'tablet:mt-5',
          )}
          aria-label={t('home.menu')}
        >
          <RoomChip to="/learn" icon={BookOpenIcon} label={t('nav.learn')} />
          <RoomChip to="/stats" icon={ChartBarIcon} label={t('nav.stats')} />
          {anonymous ? (
            <RoomChip to="/auth" icon={UserIcon} label={t('auth.signIn')} />
          ) : (
            <RoomChip to="/profile" icon={UserIcon} label={t('nav.profile')} />
          )}
          <RoomChip to="/settings" icon={SlidersHorizontalIcon} label={t('nav.settings')} />
        </nav>
      </Screen>
    </div>
  );
}

/**
 * Play again: the last game this player started from Play, one tap away.
 *
 * The door names the game it replays, so a player knows what they are about
 * to sit down to. A quick match needs a session, and a device without one
 * goes through sign-in first, the way Play setup sends it.
 */
function AgainDoor({
  game,
  hasSession,
}: {
  readonly game: LastGame;
  readonly hasSession: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const href = lastGameHref(game);
  return (
    <MenuTile
      size="poster"
      to={game.kind === 'queue' && !hasSession ? signInHref(href) : href}
      lead={
        <ArrowClockwiseIcon
          weight="bold"
          aria-hidden="true"
          focusable="false"
          className="size-7 flex-none tablet:size-8"
        />
      }
      label={t('home.again.title')}
      hint={describeLastGame(t, game)}
    />
  );
}

/** The line under Play again: the game, and who it is against. */
function describeLastGame(t: TFunction, game: LastGame): string {
  if (game.kind === 'queue') {
    const format = t(`format.${game.format}`);
    return game.variant === 'cross'
      ? t('home.again.quickMatchCross', { format })
      : t('home.again.quickMatch', { format });
  }
  const name = t(game.variant === 'cross' ? 'setup.cross' : 'setup.standard');
  if (game.kind === 'pass') {
    return t('home.again.passPlay', { game: name, players: game.players });
  }
  return t('home.again.bots', {
    game: name,
    count: game.players - 1,
    difficulty: t(`setup.${game.difficulty}`),
  });
}

/**
 * The two-minute invitation to learn Cross-Cribbage, between two ink rules
 * under the doors: the estimate on the left, then the question and the lemon
 * button. On a phone the question stands over the button beside the estimate;
 * from the tablet breakpoint all three share one row (`home-invitation`).
 *
 * It takes the stack's width and never sets it (`w-0 min-w-full`): the
 * lockup's name sets the column, as it does for every door. No close button;
 * it goes when the player finishes the walkthrough or a Cross game
 * (`docs/dev/cross_invitation.md`).
 */
function CrossInvitation(): React.JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className="home-invitation mt-6 grid w-0 min-w-full items-center gap-x-5 gap-y-2 border-y-2 border-ink py-5 tablet:mt-7"
    >
      <p className="row-span-2 flex flex-col items-center border-r-2 border-ink pr-5 tablet:row-span-1">
        <span className="text-sm text-muted">{t('home.crossNudge.learnIn')}</span>
        <span className="text-6xl leading-none font-extrabold tracking-tight tablet:text-7xl">
          {t('home.crossNudge.minutesCount')}
        </span>
        <span className="text-xs font-extrabold tracking-widest uppercase">
          {t('home.crossNudge.minutesUnit')}
        </span>
      </p>
      <h2 id={titleId} className="text-3xl leading-tight font-extrabold tracking-tight">
        {t('home.crossNudge.twoMinuteTitle')}
      </h2>
      <ButtonLink to="/learn/cross" variant="primary" className="justify-self-start">
        {t('home.crossNudge.learnCross')}
        <ArrowRightIcon size={20} aria-hidden="true" focusable="false" />
      </ButtonLink>
    </section>
  );
}

/**
 * The player, in the corner: the head alone, squared at the table's corner
 * radius, in a plain ink ring. Off the table there is no side, so it wears no
 * team color. A player with no account wears the guest silhouette.
 *
 * Decorative: the Profile or Sign in chip is the control, and the head is
 * hidden from assistive technology so the screen does not name the player
 * twice. It stands inside the notch's safe area (`home.css`).
 */
function CornerHead({ guest }: { readonly guest: boolean }): React.JSX.Element {
  return (
    <div className="home-corner absolute">
      <Avatar kind={guest ? 'guest' : 'you'} side="rows" size="panel" outline="plain" />
    </div>
  );
}
